import { useEffect, useRef, useState } from "react";

/**
 * Custom hook for debouncing a search input.
 *
 * @param {string} initialSearchTerm - The initial search term.
 * @param {number} debounceDelay - The delay in milliseconds for debouncing the search term.
 * @returns An object containing the current search input, the debounced search term, and a function to update the search input.
 */
const useDebouncedSearch = (initialSearchTerm = "", debounceDelay = 500) => {
  const [searchInput, setSearchInput] = useState(initialSearchTerm);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(initialSearchTerm);
  const searchDebounceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounceTimeout.current) {
      clearTimeout(searchDebounceTimeout.current);
    }

    searchDebounceTimeout.current = setTimeout(() => {
      setDebouncedSearchTerm(searchInput);
    }, debounceDelay);

    // Cleanup
    return () => {
      if (searchDebounceTimeout.current) {
        clearTimeout(searchDebounceTimeout.current);
      }
    };
  }, [searchInput, debounceDelay]);

  return { searchInput, debouncedSearchTerm, setSearchInput };
};

export default useDebouncedSearch;
